import type { ProductDocumentType } from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createDocumentType,
  deleteDocumentType,
  getDocumentTypes,
  updateDocumentType,
} from "@/lib/api/product-documents";

const documentTypesKey = (cid: string | null) =>
  ["portfolioCollections", cid, "documentTypes"] as const;
const productDocumentsOverviewKey = (cid: string) =>
  ["portfolioCollections", cid, "productDocumentsOverview"] as const;
// Prefix of every per-product documents key (see keys.productDocuments in
// use-portfolio-collections.ts) — a type change affects all products at once.
const productDocumentsKey = (cid: string) =>
  ["portfolioCollections", cid, "productDocuments"] as const;

export function useDocumentTypes(collectionId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: documentTypesKey(collectionId),
    queryFn: () => getDocumentTypes(collectionId!),
    enabled: !!collectionId,
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: documentTypesKey(collectionId) });
  return { documentTypes: (data ?? []) as ProductDocumentType[], error, isLoading, mutate };
}

export function useCreateDocumentType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      collectionId,
      data,
    }: {
      collectionId: string;
      data: Parameters<typeof createDocumentType>[1];
    }) => createDocumentType(collectionId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: documentTypesKey(collectionId) });
      qc.invalidateQueries({ queryKey: productDocumentsOverviewKey(collectionId) });
      qc.invalidateQueries({ queryKey: productDocumentsKey(collectionId) });
    },
  });
}

export function useUpdateDocumentType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      collectionId,
      typeId,
      data,
    }: {
      collectionId: string;
      typeId: string;
      data: Parameters<typeof updateDocumentType>[2];
    }) => updateDocumentType(collectionId, typeId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: documentTypesKey(collectionId) });
      qc.invalidateQueries({ queryKey: productDocumentsOverviewKey(collectionId) });
      qc.invalidateQueries({ queryKey: productDocumentsKey(collectionId) });
    },
  });
}

export function useDeleteDocumentType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ collectionId, typeId }: { collectionId: string; typeId: string }) =>
      deleteDocumentType(collectionId, typeId),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: documentTypesKey(collectionId) });
      qc.invalidateQueries({ queryKey: productDocumentsOverviewKey(collectionId) });
      qc.invalidateQueries({ queryKey: productDocumentsKey(collectionId) });
    },
  });
}
